import { getEntityRouteParam } from './entityIdentifier';

let lastBreadcrumbMeta = null;

/**
 * Classifies a native UI resource path so callers can decide whether to keep
 * or clear the remembered breadcrumb meta.
 * list: `/schemas`, schema: create/edit, object: records list, record: record show.
 */
export const classifyNativeUiResource = resource => {
  const path = normalizeResourcePath(resource);
  if (/^\/schemas\/[^/]+\/records\/[^/]+$/.test(path)) {
    return 'record';
  }
  if (/^\/schemas\/[^/]+\/records$/.test(path)) {
    return 'object';
  }
  if (path === '/schemas/create' || /^\/schemas\/edit\/[^/]+$/.test(path)) {
    return 'schema';
  }
  return 'list';
};

function normalizeResourcePath(resource) {
  if (!resource) {
    return '/schemas';
  }
  return String(resource).split('?')[0].replace(/\/+$/, '') || '/schemas';
}

/**
 * Remembers object/record breadcrumb meta across in-iframe navigations.
 * List/create/edit routes clear it. Object/record emits that omit meta (e.g. while
 * entity data is still loading) reuse the last named object so an early ROUTE_CHANGE
 * cannot wipe out the host's breadcrumbs.
 */
export const resolveRouteChangeMeta = (resource, meta) => {
  const page = classifyNativeUiResource(resource);
  if (page === 'list' || page === 'schema') {
    lastBreadcrumbMeta = null;
    return null;
  }
  if (meta?.object) {
    if (meta.object.name) {
      lastBreadcrumbMeta = meta;
    }
    return meta;
  }
  return lastBreadcrumbMeta;
};

export const resetLastRouteChangeMeta = () => {
  lastBreadcrumbMeta = null;
};

/**
 * Builds ROUTE_CHANGE meta for the host breadcrumb bar.
 */
export function buildRouteChangeMeta({ entity, record, displayId, routeParam } = {}) {
  if (!entity?.name && !entity?.title) {
    return null;
  }

  const param = routeParam ?? getEntityRouteParam(entity);
  if (!param) {
    return null;
  }

  const objectPath = `/schemas/${param}/records`;
  const meta = {
    object: {
      name: entity.title ?? entity.name,
      id: String(param),
      path: objectPath,
    },
  };

  const recordId = displayId ?? record?.display_id ?? record?.id;
  if (recordId != null && recordId !== '') {
    meta.record = {
      id: String(recordId),
      name: String(recordId),
      path: `${objectPath}/${recordId}`,
    };
  }

  return meta;
}
